import LineChartComponent from "@/pages/tenant-admin/insights/components/charts/LineChartComponent";
import { FC, useEffect, useState } from "react";
import Statistics from "./components/Statistics";
import Filters from "./components/Filters";
import type { DateRange } from "@capacit-com/lara-law-shared-ui";
import { DateTime } from "luxon";
import ConversationTable from "./components/ConversationTable";
import useStatistics from "@/hooks/useStatistics";
import {
  ConversationStatisticResponse,
  DocumentSourceTypes,
} from "@/models/statistic/Statistic";
import MostUsedDocGraph from "./components/MostUsedDocGraph";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";

const InsightsView: FC = () => {
  const { useGetStatistics } = useStatistics();

  const [data, setData] = useState<ConversationStatisticResponse>();
  const [presetText, setPresetText] = useState<string | undefined>();
  const [presetTextCompared, setPresetTextCompared] = useState<
    string | undefined
  >();

  const defaultDateRange = {
    from: DateTime.now().minus({ days: 7 }).startOf("day").toJSDate(),
    to: DateTime.now().endOf("day").toJSDate(),
  };

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: defaultDateRange.from,
    to: defaultDateRange.to,
  });

  const defaultCompareDateRange = {
    from: DateTime.now().minus({ days: 30 }).startOf("day").toJSDate(),
    to: DateTime.now().endOf("day").toJSDate(),
  };
  const [compareDateRange, setCompareDateRange] = useState<
    DateRange | undefined
  >({
    from: defaultCompareDateRange.from,
    to: defaultCompareDateRange.to,
  });

  const fetchedStats = useGetStatistics(
    dateRange?.from?.toISOString() ?? defaultDateRange.from.toISOString(),
    dateRange?.to?.toISOString() ?? defaultDateRange.to.toISOString(),
    compareDateRange?.from?.toISOString() ??
      defaultDateRange.from.toISOString(),
    compareDateRange?.to?.toISOString() ?? defaultDateRange.to.toISOString()
  );

  useEffect(() => {
    setPresetText("Sidste 7 dage");
    setPresetTextCompared("Sidste 30 dage");
  }, []);

  useEffect(() => {
    if (fetchedStats.isFetched && fetchedStats.data) {
      setData(fetchedStats.data);
    }
  }, [fetchedStats.data, fetchedStats.isFetched]);

  const statisticsArray = [
    {
      title: "Total antal samtaler",
      value: data?.currentTotals.totalConversations,
      percentage: data?.growth.conversationGrowth,
      tooltip:
        "Totalt antal samtaler er antallet af samtaler sammen med antallet af Word samtaler",
    },
    {
      title: "Total antal beskeder",
      value: data?.currentTotals.totalMessages,
      percentage: data?.growth.messageGrowth,
      tooltip:
        "Totalt antal beskeder er antallet af beskeder sammen med antallet af Word beskeder",
    },
    {
      title: "Gennemsnitslige bedømmelser",
      value: data?.currentTotals.averageRating.toFixed(2),
      percentage: data?.growth.ratingGrowth,
    },

    {
      title: "Antal samtaler i Word",
      value: data?.currentTotals.totalWordConversations,
      percentage: data?.growth.wordConversationGrowth,
    },
    {
      title: "Antal beskeder i Word",
      value: data?.currentTotals.totalWordMessages,
      percentage: data?.growth.wordMessageGrowth,
    },
  ];

  return (
    <div className="space-y-4 p-4 xl:px-8 transition-all duration-300">
      <div className="flex flex-row justify-end gap-2 items-end">
        <Filters
          presetText={presetText}
          setPresetText={setPresetText}
          date={dateRange}
          setDate={setDateRange}
        />
        <Filters
          presetText={presetTextCompared}
          setPresetText={setPresetTextCompared}
          titlePrefix="Sammenlignet med"
          date={compareDateRange}
          setDate={setCompareDateRange}
        />
      </div>
      <Statistics
        statisticsConfig={statisticsArray}
        isLoading={fetchedStats.isLoading}
        isError={fetchedStats.isError}
      />
      <LineChartComponent
        graphStats={data?.graphStats}
        isLoading={fetchedStats.isLoading}
        type="conversations"
      />
      <LineChartComponent
        graphStats={data?.graphStats}
        isLoading={fetchedStats.isLoading}
        type="messages"
      />
      <MostUsedDocGraph type={DocumentSourceTypes.Workspace} />
      <MostUsedDocGraph type={DocumentSourceTypes.Law} />
      <PermissionGate allOf={[Permission.ReadAllUsersConversations]}>
        <div className="overflow-auto border rounded-lg bg-card">
          <ConversationTable dateRange={dateRange} />
        </div>
      </PermissionGate>
    </div>
  );
};

export default InsightsView;
